import React, { useState, useRef, useEffect } from 'react';
import {
    TextField,
    Box,
    Grid,
    Typography,
    Button,
    IconButton,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Pagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadScript, StandaloneSearchBox } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { approveTrendOpportunity, rejectTrendOpportunity, trendActionError } from '../services/trendProxyService';

// ═══════════════════════════════════════════════════════════════════════════
// API CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

// whook, reads only. Approve and reject go through mgmt-backend's proxy (trendProxyService).
const API_BASE_URL = process.env.REACT_APP_WHOOK_API_URL || 'https://api.whook.ai';

// ═══════════════════════════════════════════════════════════════════════════
// DUMMY DATA (kept as-is)
// ═══════════════════════════════════════════════════════════════════════════

const initialMarkets = [
    { id: 1, country: 'United States', state: 'California', city: 'Los Angeles', teamLeader: 'John Doe', coverage: 80, revenue: 150000, margin: 20 },
    { id: 2, country: 'United States', state: 'California', city: 'San Francisco', teamLeader: 'Jane Smith', coverage: 75, revenue: 120000, margin: 18 },
    { id: 3, country: 'United States', state: 'New York', city: 'New York City', teamLeader: 'Alice Johnson', coverage: 85, revenue: 200000, margin: 22 },
    { id: 4, country: 'United States', state: 'Florida', city: 'Miami', teamLeader: 'Bob Brown', coverage: 65, revenue: 90000, margin: 15 },
    { id: 5, country: 'United States', state: 'Texas', city: 'Austin', teamLeader: 'Chris Lee', coverage: 90, revenue: 130000, margin: 25 },
    { id: 6, country: 'Canada', state: 'Ontario', city: 'Toronto', teamLeader: 'Emily Davis', coverage: 88, revenue: 140000, margin: 19 },
    { id: 7, country: 'Canada', state: 'British Columbia', city: 'Vancouver', teamLeader: 'Michael Scott', coverage: 70, revenue: 110000, margin: 17 },
    { id: 8, country: 'United Kingdom', state: 'England', city: 'London', teamLeader: 'David Williams', coverage: 95, revenue: 220000, margin: 28 },
    { id: 9, country: 'Australia', state: 'New South Wales', city: 'Sydney', teamLeader: 'Olivia Taylor', coverage: 85, revenue: 180000, margin: 23 },
    { id: 10, country: 'Australia', state: 'Victoria', city: 'Melbourne', teamLeader: 'Liam Wilson', coverage: 82, revenue: 160000, margin: 21 },
];

// ═══════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

const Header = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
});

const AddMarketForm = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: '#252525',
    borderRadius: '8px',
    border: '1px solid #333',
    color: '#fff',
});

const StyledTableContainer = styled(TableContainer)({
    backgroundColor: '#252525',
    borderRadius: '12px',
    boxShadow: 'none',
    border: '1px solid #333',
});

const CompactTableCell = styled(TableCell)({
    padding: '16px 20px',
    fontSize: '1rem',
    '@media (max-width: 768px)': {
        display: 'block',
        width: '100%',
        padding: '12px 0',
        fontSize: '0.9rem',
        textAlign: 'left',
    },
    color: '#fff',
    cursor: 'pointer',
    '&:hover': {
        color: '#12c099',
    },
});

const CompactTableRow = styled(TableRow)({
    '@media (max-width: 768px)': {
        display: 'block',
        width: '100%',
        marginBottom: '20px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
        borderRadius: '8px',
        backgroundColor: '#202020',
        overflow: 'hidden',
    },
});

const PaginationBox = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
    '@media (max-width: 768px)': {
        marginTop: '10px',
    },
});

const StyledTextField = styled(TextField)({
    '& .MuiInputBase-input': {
        color: '#12c099',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#12c099',
        },
        '&:hover fieldset': {
            borderColor: '#12c099',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#12c099',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#12c099',
    },
});

const StyledTabs = styled(Tabs)({
    marginBottom: '20px',
    maxWidth: '100%',      // ADD THIS
    overflow: 'hidden',     // ADD THIS
    '& .MuiTab-root': {
        color: '#888',
        '&.Mui-selected': {
            color: '#12c099',
        },
    },
    '& .MuiTabs-indicator': {
        backgroundColor: '#12c099',
    },
});

const OpportunityCard = styled(Paper)({
    backgroundColor: '#2d2d2d',
    padding: '20px',
    marginBottom: '16px',
    borderRadius: '12px',
    border: '1px solid #404040',
    boxShadow: 'none',
    '&:hover': {
        borderColor: '#12c099',
        backgroundColor: '#333',
    },
});

const ScoreChip = styled(Chip)(({ score }) => ({
    backgroundColor: score >= 80 ? '#2e7d32' : score >= 60 ? '#f57c00' : '#d32f2f',
    color: '#fff',
    fontWeight: 'bold',
}));

const DemandChip = styled(Chip)(({ demandtype }) => ({
    backgroundColor:
        demandtype === 'proven' ? '#1976d2' :
            demandtype === 'emerging' ? '#9c27b0' :
                demandtype === 'established' ? '#ed6c02' : '#666',
    color: '#fff',
    fontSize: '0.75rem',
}));

// ═══════════════════════════════════════════════════════════════════════════
// REJECTION REASONS
// ═══════════════════════════════════════════════════════════════════════════

const REJECTION_REASONS = [
    { value: 'noise_daily_spike', label: 'Daily Spike / Noise', description: 'Regular season game, daily news cycle' },
    { value: 'noise_weather', label: 'Weather Related', description: 'Weather-driven search spike' },
    { value: 'not_experiential', label: 'Not Experiential', description: "Can't be turned into an experience" },
    { value: 'wrong_timing', label: 'Wrong Timing', description: 'Off-season, past event' },
    { value: 'wrong_market', label: 'Wrong Market', description: 'Not relevant to this city' },
    { value: 'low_demand', label: 'Low Demand', description: 'Not enough interest' },
    { value: 'too_competitive', label: 'Too Competitive', description: 'Market saturated' },
    { value: 'operational_impossible', label: 'Operationally Impossible', description: "Can't execute (no venues, instructors)" },
    { value: 'duplicate', label: 'Duplicate', description: 'Already have similar opportunity' },
    { value: 'other', label: 'Other', description: 'Other reason' },
];

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const Markets = () => {
    // Existing markets state
    const [markets, setMarkets] = useState(initialMarkets);
    const [newMarket, setNewMarket] = useState({
        country: '',
        state: '',
        city: '',
        teamLeader: '',
        coverage: '',
        revenue: '',
        margin: '',
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const rowsPerPage = 5;
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('country');
    const [expanded, setExpanded] = useState(false);
    const searchBoxRef = useRef(null);
    const navigate = useNavigate();

    // NEW: Tab state
    const [activeTab, setActiveTab] = useState(0);

    // NEW: Trend opportunities state
    const [opportunities, setOpportunities] = useState([]);
    const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
    const [opportunitiesError, setOpportunitiesError] = useState(null);
    const [selectedCity, setSelectedCity] = useState('Los Angeles');
    const [opportunityPage, setOpportunityPage] = useState(1);
    const opportunitiesPerPage = 10;

    // NEW: Rejection dialog state
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [selectedOpportunity, setSelectedOpportunity] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectionExplanation, setRejectionExplanation] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // ═══════════════════════════════════════════════════════════════════════
    // FETCH OPPORTUNITIES
    // ═══════════════════════════════════════════════════════════════════════

    const fetchOpportunities = async (city, isInitialLoad = false) => {
        // Only show loading spinner if we have no data yet
        if (!isInitialLoad && opportunities.length === 0) {
            setOpportunitiesLoading(true);
        }
        setOpportunitiesError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/v1/agent/discovery/opportunities`, {
                params: {
                    city,
                    status: 'pending_review',
                    sortBy: 'opportunityScore',
                    sortOrder: 'desc',
                    limit: 50
                }
            });
            setOpportunities(response.data.data || []);
        } catch (err) {
            console.error('Failed to fetch opportunities:', err);
            setOpportunitiesError(err.response?.data?.error || 'Failed to load opportunities');
        } finally {
            setOpportunitiesLoading(false);
        }
    };

    // Pre-fetch opportunities on component mount
    useEffect(() => {
        fetchOpportunities(selectedCity, true);
    }, []);

    // Fetch when city changes (but not on tab change since we pre-fetched)
    useEffect(() => {
        if (activeTab === 1) {
            fetchOpportunities(selectedCity);
        }
    }, [selectedCity]);

    // ═══════════════════════════════════════════════════════════════════════
    // APPROVE / REJECT HANDLERS
    // ═══════════════════════════════════════════════════════════════════════

    const handleApprove = async (opportunity) => {
        setActionLoading(true);
        try {
            await approveTrendOpportunity(opportunity._id);
            // Remove from list
            setOpportunities(prev => prev.filter(o => o._id !== opportunity._id));
        } catch (err) {
            console.error('Failed to approve:', err);
            alert(trendActionError(err, 'Failed to approve opportunity'));
        } finally {
            setActionLoading(false);
        }
    };

    const openRejectDialog = (opportunity) => {
        setSelectedOpportunity(opportunity);
        setRejectionReason('');
        setRejectionExplanation('');
        setRejectDialogOpen(true);
    };

    const handleReject = async () => {
        if (!rejectionReason) {
            alert('Please select a rejection reason');
            return;
        }

        setActionLoading(true);
        try {
            await rejectTrendOpportunity(selectedOpportunity._id, {
                reason: rejectionReason,
                explanation: rejectionExplanation,
                shouldTeachAgent: true
            });
            // Remove from list
            setOpportunities(prev => prev.filter(o => o._id !== selectedOpportunity._id));
            setRejectDialogOpen(false);
        } catch (err) {
            console.error('Failed to reject:', err);
            alert(trendActionError(err, 'Failed to reject opportunity'));
        } finally {
            setActionLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // EXISTING MARKET HANDLERS (unchanged)
    // ═══════════════════════════════════════════════════════════════════════

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleCountryClick = (country) => {
        navigate(`/countryoverview/${encodeURIComponent(country)}`);
    };

    const handleStateClick = (state) => {
        navigate(`/stateoverview/${encodeURIComponent(state)}`);
    };

    const handleCityClick = (city) => {
        navigate(`/cityoverview/${encodeURIComponent(city)}`);
    };

    const handleAddMarket = () => {
        const newId = markets.length + 1;
        setMarkets([...markets, { id: newId, ...newMarket }]);
        setNewMarket({
            country: '',
            state: '',
            city: '',
            teamLeader: '',
            coverage: '',
            revenue: '',
            margin: '',
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewMarket((prev) => ({ ...prev, [name]: value }));
    };

    const handlePlacesChanged = () => {
        const places = searchBoxRef.current.getPlaces();
        if (places.length > 0) {
            const place = places[0];
            const addressComponents = place.address_components;

            const city = addressComponents.find((comp) =>
                comp.types.includes('locality')
            )?.long_name;

            const state = addressComponents.find((comp) =>
                comp.types.includes('administrative_area_level_1')
            )?.short_name;

            const country = addressComponents.find((comp) =>
                comp.types.includes('country')
            )?.long_name;

            setNewMarket((prev) => ({
                ...prev,
                city: city || '',
                state: state || '',
                country: country || '',
            }));
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // FILTERED & SORTED DATA
    // ═══════════════════════════════════════════════════════════════════════

    const filteredMarkets = markets.filter(market =>
        market.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        market.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
        market.city.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedMarkets = filteredMarkets.sort((a, b) => {
        if (order === 'asc') {
            return a[orderBy].localeCompare(b[orderBy]);
        }
        return b[orderBy].localeCompare(a[orderBy]);
    });

    const paginatedMarkets = sortedMarkets.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const paginatedOpportunities = opportunities.slice(
        (opportunityPage - 1) * opportunitiesPerPage,
        opportunityPage * opportunitiesPerPage
    );

    // ═══════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════

    return (
        <Box sx={{ padding: '1.5rem', backgroundColor: '#1c1c1c', minHeight: '100vh', color: '#fff' }}>
            <Header>
                <Typography variant="h4">Markets</Typography>
                <IconButton onClick={() => setExpanded(!expanded)} sx={{ color: '#12c099' }}>
                    <AddCircleIcon />
                    <Typography variant="body1" sx={{ marginLeft: '8px', color: '#12c099' }}>
                        Add New Market
                    </Typography>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Header>

            {/* TABS: Markets | Trend Opportunities */}
            <StyledTabs
                value={activeTab}
                onChange={(e, v) => {
                    setActiveTab(v);
                    // Reset pagination when switching tabs
                    if (v === 0) setPage(1);
                    if (v === 1) setOpportunityPage(1);
                }}
            >
                <Tab label="Markets" />
                <Tab
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon fontSize="small" />
                            Trend Opportunities
                            {opportunities.length > 0 && (
                                <Chip
                                    label={opportunities.length}
                                    size="small"
                                    sx={{ backgroundColor: '#12c099', color: '#000', height: 20 }}
                                />
                            )}
                        </Box>
                    }
                />
            </StyledTabs>

            {/* TAB 0: Original Markets Table */}
            {activeTab === 0 && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            placeholder="Search by city, state, or country"
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                backgroundColor: '#202020',
                                color: '#fff',
                                marginBottom: '20px',
                                '& .MuiInputBase-input': { color: '#12c099' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#12c099' },
                                    '&:hover fieldset': { borderColor: '#12c099' },
                                    '&.Mui-focused fieldset': { borderColor: '#12c099' },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Collapse in={expanded}>
                            <AddMarketForm>
                                <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY} libraries={['places']}>
                                    <StandaloneSearchBox onLoad={ref => (searchBoxRef.current = ref)} onPlacesChanged={handlePlacesChanged}>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Enter city, state, or country"
                                            sx={{ backgroundColor: '#202020', color: '#fff', width: '100%' }}
                                            inputProps={{ style: { color: '#fff' } }}
                                        />
                                    </StandaloneSearchBox>
                                </LoadScript>
                                <StyledTextField label="City" name="city" value={newMarket.city} onChange={handleInputChange} variant="outlined" />
                                <StyledTextField label="State" name="state" value={newMarket.state} onChange={handleInputChange} variant="outlined" />
                                <StyledTextField label="Country" name="country" value={newMarket.country} onChange={handleInputChange} variant="outlined" />
                                <Button variant="contained" sx={{ backgroundColor: '#12c099', color: '#000', marginTop: '10px' }} onClick={handleAddMarket}>
                                    Add Market
                                </Button>
                            </AddMarketForm>
                        </Collapse>
                    </Grid>
                    <Grid item xs={12}>
                        <StyledTableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {['country', 'state', 'city', 'teamLeader', 'coverage', 'revenue', 'margin'].map((col) => (
                                            <CompactTableCell key={col}>
                                                <TableSortLabel
                                                    active={orderBy === col}
                                                    direction={orderBy === col ? order : 'asc'}
                                                    onClick={() => handleSortRequest(col)}
                                                    style={{ color: '#12c099' }}
                                                >
                                                    {col.charAt(0).toUpperCase() + col.slice(1).replace(/([A-Z])/g, ' $1')}
                                                </TableSortLabel>
                                            </CompactTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedMarkets.map((market) => (
                                        <CompactTableRow key={market.id}>
                                            <CompactTableCell onClick={() => handleCountryClick(market.country)}>{market.country}</CompactTableCell>
                                            <CompactTableCell onClick={() => handleStateClick(market.state)}>{market.state}</CompactTableCell>
                                            <CompactTableCell onClick={() => handleCityClick(market.city)}>{market.city}</CompactTableCell>
                                            <CompactTableCell>{market.teamLeader}</CompactTableCell>
                                            <CompactTableCell>{market.coverage}%</CompactTableCell>
                                            <CompactTableCell>${market.revenue.toLocaleString()}</CompactTableCell>
                                            <CompactTableCell>{market.margin}%</CompactTableCell>
                                        </CompactTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </Grid>
                    <PaginationBox>
                        <Pagination
                            count={Math.ceil(filteredMarkets.length / rowsPerPage)}
                            page={page}
                            onChange={(event, value) => setPage(value)}
                            sx={{ button: { color: '#12c099' }, ul: { color: '#12c099' } }}
                        />
                    </PaginationBox>
                </Grid>
            )}

            {/* TAB 1: Trend Opportunities */}
            {activeTab === 1 && (
                <Box sx={{ minHeight: '400px' }}>
                    {/* City selector & refresh */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel sx={{ color: '#12c099' }}>City</InputLabel>
                            <Select
                                value={selectedCity}
                                onChange={(e) => setSelectedCity(e.target.value)}
                                label="City"
                                sx={{
                                    color: '#12c099',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#12c099' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#12c099' },
                                }}
                            >
                                <MenuItem value="Los Angeles">Los Angeles</MenuItem>
                                <MenuItem value="San Francisco">San Francisco</MenuItem>
                                <MenuItem value="New York City">New York City</MenuItem>
                                <MenuItem value="Miami">Miami</MenuItem>
                                <MenuItem value="Austin">Austin</MenuItem>
                            </Select>
                        </FormControl>
                        <IconButton onClick={() => fetchOpportunities(selectedCity)} sx={{ color: '#12c099' }}>
                            <RefreshIcon />
                        </IconButton>
                        <Typography variant="body2" sx={{ color: '#888' }}>
                            {opportunities.length} opportunities pending review
                        </Typography>
                    </Box>

                    {/* Loading / Error */}
                    {opportunitiesLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: '#12c099' }} />
                        </Box>
                    )}

                    {opportunitiesError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {opportunitiesError}
                        </Alert>
                    )}

                    {/* Opportunities List */}
                    {!opportunitiesLoading && !opportunitiesError && (
                        <>
                            {paginatedOpportunities.map((opp) => (
                                <OpportunityCard key={opp._id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box>
                                            <Typography variant="h6" sx={{ color: '#fff', mb: 0.5 }}>
                                                {opp.trend}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <ScoreChip label={`Score: ${opp.opportunityScore}`} size="small" score={opp.opportunityScore} />
                                                <DemandChip label={opp.signals?.demandType || 'unknown'} size="small" demandtype={opp.signals?.demandType} />
                                                <Chip label={opp.trendType} size="small" sx={{ backgroundColor: '#444', color: '#fff' }} />
                                                {opp.timing?.urgency === 'immediate' && (
                                                    <Chip label="🔥 URGENT" size="small" sx={{ backgroundColor: '#d32f2f', color: '#fff' }} />
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Approve">
                                                <IconButton
                                                    onClick={() => handleApprove(opp)}
                                                    disabled={actionLoading}
                                                    sx={{ color: '#4caf50' }}
                                                >
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reject">
                                                <IconButton
                                                    onClick={() => openRejectDialog(opp)}
                                                    disabled={actionLoading}
                                                    sx={{ color: '#f44336' }}
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {/* Context */}
                                    <Typography variant="body2" sx={{ color: '#e0e0e0', mb: 1 }}>
                                        {opp.trendContext}
                                    </Typography>

                                    {/* TikTok Stats */}
                                    {opp.signals?.tiktokViews && (
                                        <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 1 }}>
                                            📱 TikTok: {opp.signals.tiktokViews.toLocaleString()} views | {opp.signals.tiktokConfidence}% confidence
                                        </Typography>
                                    )}

                                    {/* Experience Ideas */}
                                    {opp.experienceIdeas?.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#12c099', mb: 0.5, fontWeight: 'bold' }}>
                                                Experience Ideas:
                                            </Typography>
                                            {opp.experienceIdeas.slice(0, 2).map((idea, idx) => (
                                                <Typography key={idx} variant="body2" sx={{ color: '#fff', ml: 1 }}>
                                                    • {idea.name} ({idea.format})
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Reasoning */}
                                    <Typography variant="body2" sx={{ color: '#9e9e9e', mt: 1, fontStyle: 'italic' }}>
                                        {opp.reasoning}
                                    </Typography>
                                </OpportunityCard>
                            ))}

                            {opportunities.length === 0 && (
                                <Typography sx={{ color: '#888', textAlign: 'center', py: 4 }}>
                                    No opportunities pending review for {selectedCity}
                                </Typography>
                            )}

                            {opportunities.length > opportunitiesPerPage && (
                                <PaginationBox>
                                    <Pagination
                                        count={Math.ceil(opportunities.length / opportunitiesPerPage)}
                                        page={opportunityPage}
                                        onChange={(e, v) => setOpportunityPage(v)}
                                        sx={{ button: { color: '#12c099' } }}
                                    />
                                </PaginationBox>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* Rejection Dialog */}
            <Dialog
                open={rejectDialogOpen}
                onClose={() => setRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#282828',
                        backgroundImage: 'none',
                    }
                }}
            >
                <DialogTitle sx={{ backgroundColor: '#282828', color: '#fff' }}>
                    Reject: {selectedOpportunity?.trend}
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#282828' }}>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel sx={{ color: '#12c099' }}>Rejection Reason</InputLabel>
                        <Select
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            label="Rejection Reason"
                            sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#12c099' } }}
                        >
                            {REJECTION_REASONS.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    <Box>
                                        <Typography>{r.label}</Typography>
                                        <Typography variant="caption" sx={{ color: '#888' }}>{r.description}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Explanation (optional)"
                        value={rejectionExplanation}
                        onChange={(e) => setRejectionExplanation(e.target.value)}
                        sx={{ mt: 2, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#12c099' }, '& .MuiInputLabel-root': { color: '#12c099' } }}
                        placeholder="e.g., Regular season Lakers game, not playoff-worthy"
                    />
                    <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
                        This feedback will help the AI avoid similar mistakes in the future.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: '#282828' }}>
                    <Button onClick={() => setRejectDialogOpen(false)} sx={{ color: '#888' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReject}
                        disabled={actionLoading || !rejectionReason}
                        sx={{ backgroundColor: '#f44336', color: '#fff', '&:hover': { backgroundColor: '#d32f2f' } }}
                    >
                        {actionLoading ? <CircularProgress size={20} /> : 'Reject & Teach AI'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Markets;
